import { formatValue, ValueTypes } from '@cfra-nextgen-frontend/shared/src/utils/valuesFormatter';
import type { S3SecurityType } from '../api/chartConfig';
import type { Perf10kSummary } from '../api/chartDataTypes';

// ─── Date helpers (ISO 'YYYY-MM-DD' → display strings) ────────────────────────

const MONTH_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Parse a 'YYYY-MM-DD' ISO date without timezone drift. */
export function parseIsoDate(iso: string): { year: number; month: number; day: number } | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** 'MM/DD/YY' — used in the summary banner and Date dropdown options. */
export function formatShortDate(iso: string): string {
    const parts = parseIsoDate(iso);
    if (!parts) return iso;
    const { year, month, day } = parts;
    const yy = String(year).slice(-2);
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${yy}`;
}

/** 'April 30, 2026' — used in the assertive NVDA announcement. */
export function formatLongDate(iso: string): string {
    const parts = parseIsoDate(iso);
    if (!parts) return iso;
    return `${MONTH_LONG[parts.month - 1]} ${parts.day}, ${parts.year}`;
}

/** 'Mar 31 2026' — used in the header subtitle. */
export function formatReportDate(iso: string): string {
    const parts = parseIsoDate(iso);
    if (!parts) return iso;
    return `${MONTH_SHORT[parts.month - 1]} ${parts.day} ${parts.year}`;
}

/** Full month name from a 1-12 month number. */
export function monthName(month: number): string {
    return MONTH_LONG[month - 1] ?? '';
}

// ─── Currency / number helpers ────────────────────────────────────────────────

/** 'USD 12,428' — currency code prefix, integer value, comma-separated, no decimals. */
export function formatCurrencyInt(value: number, currency: string): string {
    const number = formatValue({ value: Math.round(value), formattingType: ValueTypes.MillionsScale });
    return currency ? `${currency} ${number}` : number?.toString();
}

// ─── Report-type labels ───────────────────────────────────────────────────────

const REPORT_TYPE_LABEL: Record<S3SecurityType, string> = {
    ETF_EQUITY: 'ETF',
    ETF_BOND: 'ETF',
    MF_EQUITY: 'Mutual Fund',
    MF_BOND: 'Mutual Fund',
    STARS_ENRT: 'Stock',
    STARS_ENRN: 'Stock',
};

/** Short label shown in the header card subtitle ('ETF', 'Mutual Fund', 'Stock'). */
export function reportTypeLabel(reportType: S3SecurityType): string {
    return REPORT_TYPE_LABEL[reportType] ?? reportType;
}

/** Lower-case noun used inside the summary sentence ('ETF', 'mutual fund', 'stock'). */
export function reportTypeNoun(reportType: S3SecurityType): string {
    if (reportType.startsWith('ETF')) return 'ETF';
    if (reportType.startsWith('MF')) return 'mutual fund';
    return 'stock';
}

// ─── Sentence builders ────────────────────────────────────────────────────────

/**
 * "04/25/21 – 04/30/26: A $10,000 investment in this ETF is worth $19,851."
 *
 * `end_display` from the API is already comma-formatted — we prepend '$'.
 */
export function buildSummarySentence(
    summary: Perf10kSummary,
    reportType: S3SecurityType,
    currency: string,
    overrideEnd?: { date: string; value: number },
): string {
    const start = formatLongDate(summary.start_date);
    const end = formatLongDate(overrideEnd ? overrideEnd.date : summary.end_date);
    const startValue = formatCurrencyInt(summary.start_value, currency);
    const endValue = overrideEnd
        ? formatCurrencyInt(overrideEnd.value, currency)
        : currency
          ? `${currency} ${summary.end_display}`
          : summary.end_display;
    return `${start} – ${end}: A ${startValue} investment in this ${reportTypeNoun(reportType)} is worth ${endValue}.`;
}

/**
 * "On April 30, 2026, a USD 10,000 investment was worth USD 12,428."
 *
 * Reserved for future use — currently the panel announces via the role="status"
 * summary banner instead.
 */
export function buildAnnouncementSentence(
    isoDate: string,
    startValue: number,
    value: number,
    currency: string,
): string {
    return `On ${formatLongDate(isoDate)}, a ${formatCurrencyInt(startValue, currency)} investment was worth ${formatCurrencyInt(value, currency)}.`;
}
